import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, BadgeInfo, ChevronDown, ChevronRight, ChevronUp, RefreshCw, ShieldAlert, Info, X } from "lucide-react";

const REMEDIATION_DISCLAIMER_STORAGE_KEY = "octopus.remediation.disclaimerSeen";

function getDisclaimerText() {
  return "Assistant may be inaccurate or incomplete. Don't always trust AI.";
}

function getValue(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  return value;
}

function getBandVariant(value) {
  const v = String(value || "").toLowerCase();
  if (v === "critical") return "critical";
  if (v === "high") return "high";
  if (v === "medium") return "medium";
  if (v === "low") return "low";
  return "";
}

function Badge({ label, value, active = false, variant = "" }) {
  return (
    <div className={`oct-impact-badge${active ? " is-active" : ""}${variant ? ` oct-impact-badge--${variant}` : ""}`}>
      <span>{label}</span>
      <strong>{getValue(value)}</strong>
    </div>
  );
}

function HoverDetail({ field, placement = "top" }) {
  if (!field) return null;

  return (
    <div className={`oct-impact-tooltip is-${placement}`} role="status" aria-live="polite">
      <div className="oct-impact-tooltip__title">
        <BadgeInfo size={14} />
        <h4>{field.label}</h4>
      </div>
      <p className="oct-impact-tooltip__summary">{field.summary}</p>
      <p className="oct-impact-tooltip__body">{field.detail}</p>
    </div>
  );
}

function ListBlock({ title, items = [], detail, placement = "top", onHover, onLeave, active = false }) {
  return (
    <div
      className={`oct-impact-list oct-impact-card-wrap${active ? " is-active" : ""}`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      {active ? <HoverDetail field={{ label: title, summary: detail?.summary, detail: detail?.detail }} placement={placement} /> : null}
      <h4>
        <BadgeInfo size={16} />
        {title}
      </h4>
      {detail?.hint ? <p className="oct-impact-list__hint">{detail.hint}</p> : null}
      {items.length ? (
        <ul>
          {items.map((item, index) => <li key={index}>{item}</li>)}
        </ul>
      ) : (
        <p>N/A</p>
      )}
    </div>
  );
}

function formatContext(result) {
  const parts = [];
  if (result?.deviceName) parts.push(`Device ${result.deviceName}`);
  if (result?.deviceIp) parts.push(`IP ${result.deviceIp}`);
  if (result?.ruleName) parts.push(`Rule ${result.ruleName}`);
  if (result?.severity) parts.push(`Severity ${result.severity}`);
  return parts.length ? parts.join(" • ") : "";
}

function toBulletList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") return value.split("\n").map((item) => item.trim()).filter(Boolean);
  return [];
}

function getErrorMessage(error) {
  const raw = String(error?.message || error || "");
  if (raw.includes("521")) {
    return "Impact analysis service is temporarily unavailable. The upstream connector returned 521 (application offline). Please retry shortly.";
  }
  return raw || "Impact analysis failed";
}

function generateFallbackRollback(rule, device) {
  const ruleName = (rule?.name || rule?.ruleName || "").toLowerCase();
  const script = String(rule?.remediationScript || "").toLowerCase();
  const deviceType = (device?.deviceType?.name || device?.deviceType || device?.type || "").toLowerCase();

  const steps = [];

  steps.push("Take a full configuration backup before applying the remediation.");

  if (/ssh/i.test(ruleName)) {
    steps.push("Re-enable the previous SSH version settings (e.g. allow SSHv1 if it was previously permitted).");
    steps.push("Verify management access via console or HTTPS before applying SSH-related changes.");
  }
  if (/password|auth|credential/i.test(ruleName)) {
    steps.push("Restore previous authentication settings from the configuration backup.");
  }
  if (/firewall|acl|access.list|policy/i.test(ruleName)) {
    steps.push("Re-apply previous ACL or firewall policy rules from the saved backup.");
  }
  if (/snmp/i.test(ruleName)) {
    steps.push("Restore previous SNMP community strings and access control entries.");
  }
  if (/ntp|time/i.test(ruleName)) {
    steps.push("Restore previous NTP server configuration.");
  }
  if (/logging|syslog/i.test(ruleName)) {
    steps.push("Restore previous syslog and logging configuration.");
  }
  if (/telnet/i.test(ruleName)) {
    steps.push("Re-enable Telnet access if it was required before this change.");
  }
  if (/http|https|web.manage/i.test(ruleName)) {
    steps.push("Restore previous HTTP/HTTPS management access configuration.");
  }
  if (/banner|motd/i.test(ruleName)) {
    steps.push("Restore previous login banner or MOTD text from backup.");
  }
  if (/encrypt|cipher|tls|ssl/i.test(ruleName)) {
    steps.push("Revert cipher suite or encryption settings to the previously configured values.");
  }

  if (/\bno\b /i.test(script) || /^no /im.test(script)) {
    steps.push("Re-add configuration lines that were removed (those prefixed with 'no') to restore the original state.");
  }
  if (/\bset\b/.test(script) && /\bconfig\b/.test(script)) {
    steps.push("Restore original parameter values using 'set' commands with the values captured in the pre-change backup.");
  }
  if (/\benable\b/.test(script)) {
    steps.push("Reverse any 'enable' commands to 'disable' where the original state was disabled.");
  }

  if (/fortinet|fortigate/i.test(deviceType)) {
    steps.push("Use FortiGate console port or HTTPS GUI to restore configuration if CLI/SSH access is disrupted.");
    steps.push("Run 'execute restore config' from FortiGate console to load the pre-change backup if needed.");
  } else if (/cisco|ios|nx.?os/i.test(deviceType)) {
    steps.push("Use console or out-of-band management port to restore the running configuration.");
    steps.push("Issue 'configure replace' with the saved backup file to roll back Cisco IOS configuration.");
  } else if (/juniper|junos/i.test(deviceType)) {
    steps.push("Run 'rollback 1' in Junos configuration mode to revert to the previous committed configuration.");
  } else if (/palo.?alto|pan.?os/i.test(deviceType)) {
    steps.push("Use PAN-OS 'revert to last saved configuration' option in the GUI or via CLI.");
  } else if (/checkpoint/i.test(deviceType)) {
    steps.push("Use SmartConsole to revert to the previous policy revision.");
  } else {
    steps.push("Use console or out-of-band access to restore configuration if remote access is disrupted.");
  }

  steps.push("Verify device connectivity and confirm management access after the rollback is complete.");

  return [...new Set(steps)];
}

function ImpactAnalysisPopup({ device, rule, result, loading, error, onReassess, onProceed, onClose }) {
  const [showDisclaimer, setShowDisclaimer] = useState(() => localStorage.getItem(REMEDIATION_DISCLAIMER_STORAGE_KEY) !== "true");
  const [hoveredField, setHoveredField] = useState(null);
  const [showContextDetails, setShowContextDetails] = useState(false);
  const [showScript, setShowScript] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const dismissDisclaimer = () => {
    localStorage.setItem(REMEDIATION_DISCLAIMER_STORAGE_KEY, "true");
    setShowDisclaimer(false);
  };

  const deviceName = device?.hostName || device?.name || "N/A";
  const deviceIp = device?.mngtIpAddress || device?.ipAddress || device?.ip || "N/A";
  const deviceType = device?.deviceType?.name || device?.deviceType || device?.type || "N/A";
  const baseline = device?.msbName || device?.baselineName || device?.complianceBaseline || "N/A";
  const ruleName = rule?.name || rule?.ruleName || rule?.rule || "N/A";
  const ruleDescription = rule?.remediationDesc || rule?.description || "N/A";
  const severity = rule?.severity || "N/A";
  const script = rule?.remediationScript || "N/A";
  const scriptLines = script !== "N/A" ? String(script).split("\n").filter((line) => line.trim().length > 0).length : 0;

  const rollbackItems = useMemo(() => {
    const fromApi = toBulletList(result?.rollbackOptions);
    if (fromApi.length) return fromApi;
    return generateFallbackRollback(rule, device);
  }, [result, rule, device]);

  const fieldDetails = useMemo(() => {
    const band = result?.band || result?.impactBand || result?.impact_band;
    const impactScore = result?.impactScore ?? result?.impact_score;
    const confidence = result?.confidenceScore ?? result?.confidence_score;
    const approve = result?.canBeApproved ?? result?.approve ?? result?.approved ?? result?.can_be_approved;
    const simulatable = result?.canBeSimulated ?? result?.simulatable ?? result?.can_be_simulated;
    const execution = result?.recommendedExecutionTime || result?.execution || result?.recommended_execution_time;

    return [
      {
        key: "impact-score",
        label: "Impact Score",
        value: impactScore != null ? `${impactScore}/100` : null,
        variant: "",
        summary: "A numeric score that estimates the expected impact of the remediation.",
        detail: `Score ${getValue(impactScore)}/100 reflects the model's estimate of how disruptive this change may be.`,
      },
      {
        key: "band",
        label: "Risk Band",
        value: band,
        variant: getBandVariant(band),
        summary: "A risk bucket for the score.",
        detail: `Band ${getValue(band)} groups the score into a higher-level risk category.`,
      },
      {
        key: "confidence",
        label: "Confidence",
        value: confidence != null ? `${confidence}/100` : null,
        variant: "",
        summary: "How confident the model is in the assessment.",
        detail: `Confidence ${getValue(confidence)}/100 indicates how certain the model is about this result.`,
      },
      {
        key: "approve",
        label: "Approved",
        value: approve ? "Yes" : "No",
        variant: approve ? "low" : "high",
        summary: "Whether the remediation can proceed.",
        detail: `Approve is ${approve ? "Yes" : "No"} based on the current assessment signals.`,
      },
      {
        key: "simulatable",
        label: "Simulatable",
        value: simulatable ? "Yes" : "No",
        variant: simulatable ? "low" : "",
        summary: "Whether the action can be safely simulated first.",
        detail: `Simulatable is ${simulatable ? "Yes" : "No"} meaning the remediation ${simulatable ? "can" : "likely cannot"} be previewed before applying.`,
      },
      {
        key: "execution",
        label: "Execution Window",
        value: execution,
        variant: "",
        summary: "Operational guidance for applying the remediation.",
        detail: getValue(execution),
      },
    ];
  }, [result]);

  const contextText = useMemo(() => formatContext(result), [result]);

  const sectionDetails = {
    impactDrivers: {
      summary: "Why the model assigned this level of impact.",
      detail: "These are the strongest signals that increased the score. They explain the main operational or security reasons behind the assessment.",
      hint: "Hover for a quick explanation of the main reasons driving impact upward.",
    },
    safetyDrivers: {
      summary: "Why the model considers the remediation safer.",
      detail: "These are the signals that reduced risk, such as easy rollback, limited scope, or no service-impacting change.",
      hint: "Hover for the low-risk signals that pulled the score down.",
    },
    explanation: {
      summary: "The model's plain-language reasoning.",
      detail: "This section summarizes what the remediation changes, what could be affected, and why the output landed at this score and band.",
      hint: "Hover to see what this summary is meant to help you review.",
    },
    preChecks: {
      summary: "Checks to confirm before running the remediation.",
      detail: "These are concrete validation steps that help reduce risk before execution. They usually cover dependencies, backups, access, and scope checks.",
      hint: "Hover to see why these checks matter before execution.",
    },
    rollback: {
      summary: "How to undo the change if needed.",
      detail: "These options describe the safest recovery path if the remediation causes unexpected behavior. A clear rollback lowers operational risk.",
      hint: "Hover to review the recovery path before proceeding.",
    },
  };

  return (
    <div className="oct-impact-modal" role="dialog" aria-modal="true" aria-labelledby="oct-impact-title">
      <div className="oct-impact-modal__backdrop" onClick={onClose} />
      <div className="oct-impact-modal__dialog" onClick={(e) => e.stopPropagation()}>
        <div className="oct-impact-modal__header">
          <div>
            <div className="oct-impact-modal__eyebrow">
              <ShieldAlert size={16} /> Impact Analysis
            </div>
            <h2 id="oct-impact-title">Review before remediation</h2>
            <p>
              Check the assessed impact for <strong>{deviceName}</strong> and <strong>{ruleName}</strong>, then decide whether to reassess or proceed.
            </p>
            <div className="oct-impact-modal__header-row">
              {contextText && (
                <div className="oct-impact-modal__context">
                  <Info size={14} />
                  <span>{contextText}</span>
                </div>
              )}
              <button
                type="button"
                className="oct-impact-modal__expand"
                onClick={() => setShowContextDetails((value) => !value)}
                aria-expanded={showContextDetails}
              >
                <span>Device and rule details</span>
                {showContextDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
            {showContextDetails ? (
              <div className="oct-impact-modal__inline-context">
                <span><strong>Device:</strong> {deviceName}</span>
                <span><strong>IP:</strong> {deviceIp}</span>
                <span><strong>Type:</strong> {deviceType}</span>
                <span><strong>Baseline:</strong> {baseline}</span>
                <span><strong>Rule:</strong> {ruleName}</span>
                <span><strong>Severity:</strong> {severity}</span>
                <span><strong>Description:</strong> {ruleDescription}</span>
              </div>
            ) : null}
          </div>
          <button type="button" className="oct-impact-modal__close" onClick={onClose} aria-label="Close impact analysis">
            <X size={18} />
          </button>
        </div>

        {showDisclaimer ? (
          <div className="oct-impact-modal__disclaimer">
            <ShieldAlert size={16} />
            <span>{getDisclaimerText()}</span>
            <button
              type="button"
              className="oct-impact-modal__disclaimer-close"
              onClick={dismissDisclaimer}
              aria-label="Dismiss remediation disclaimer"
            >
              <X size={14} />
            </button>
          </div>
        ) : null}

        <div className="oct-impact-modal__body">
          {loading ? (
            <div className="oct-impact-loading">Running impact analysis...</div>
          ) : error ? (
            <div className="oct-impact-error">{getErrorMessage(error)}</div>
          ) : result ? (
            <>
              <div className="oct-impact-scorecard">
                {fieldDetails.slice(0, 3).map((field) => (
                  <div
                    key={field.key}
                    className="oct-impact-card-wrap"
                    onMouseEnter={() => setHoveredField(field.key)}
                    onMouseLeave={() => setHoveredField(null)}
                  >
                    {hoveredField === field.key ? <HoverDetail field={field} placement="bottom" /> : null}
                    <Badge label={field.label} value={field.value} variant={field.variant} active={hoveredField === field.key} />
                  </div>
                ))}
              </div>

              <div className="oct-impact-grid">
                {fieldDetails.slice(3).map((field) => (
                  <div
                    key={field.key}
                    className="oct-impact-grid__cell oct-impact-card-wrap"
                    onMouseEnter={() => setHoveredField(field.key)}
                    onMouseLeave={() => setHoveredField(null)}
                  >
                    {hoveredField === field.key ? <HoverDetail field={field} placement="bottom" /> : null}
                    <Badge label={field.label} value={field.value} variant={field.variant} active={hoveredField === field.key} />
                  </div>
                ))}
              </div>

              <div
                className={`oct-impact-summary oct-impact-card-wrap${hoveredField === "explanation" ? " is-active" : ""}`}
                onMouseEnter={() => setHoveredField("explanation")}
                onMouseLeave={() => setHoveredField(null)}
              >
                {hoveredField === "explanation" ? <HoverDetail field={{ label: "Explanation", ...sectionDetails.explanation }} placement="top" /> : null}
                <h4><BadgeInfo size={16} /> Explanation</h4>
                <p>{result.explanation}</p>
              </div>

              <div className="oct-impact-script-panel">
                <button
                  type="button"
                  className="oct-impact-script-panel__toggle"
                  onClick={() => setShowScript((value) => !value)}
                  aria-expanded={showScript}
                >
                  <span>
                    <BadgeInfo size={16} /> Remediation Script
                  </span>
                  <span className="oct-impact-script-panel__meta">
                    {scriptLines ? `${scriptLines} line${scriptLines === 1 ? "" : "s"}` : "N/A"}
                    {showScript ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </span>
                </button>
                {showScript ? <pre>{script}</pre> : null}
              </div>

              <div className="oct-impact-columns">
                <ListBlock
                  title="Top impact drivers"
                  items={toBulletList(result.topImpactDrivers)}
                  detail={sectionDetails.impactDrivers}
                  placement="top"
                  onHover={() => setHoveredField("top-impact-drivers")}
                  onLeave={() => setHoveredField(null)}
                  active={hoveredField === "top-impact-drivers"}
                />
                <ListBlock
                  title="Top safety drivers"
                  items={toBulletList(result.topSafetyDrivers)}
                  detail={sectionDetails.safetyDrivers}
                  placement="top"
                  onHover={() => setHoveredField("top-safety-drivers")}
                  onLeave={() => setHoveredField(null)}
                  active={hoveredField === "top-safety-drivers"}
                />
                <ListBlock
                  title="Pre-checks"
                  items={toBulletList(result.requiredPreChecks)}
                  detail={sectionDetails.preChecks}
                  placement="top"
                  onHover={() => setHoveredField("pre-checks")}
                  onLeave={() => setHoveredField(null)}
                  active={hoveredField === "pre-checks"}
                />
                <ListBlock
                  title="Rollback options"
                  items={rollbackItems}
                  detail={sectionDetails.rollback}
                  placement="top"
                  onHover={() => setHoveredField("rollback-options")}
                  onLeave={() => setHoveredField(null)}
                  active={hoveredField === "rollback-options"}
                />
              </div>
            </>
          ) : null}
        </div>

        <div className="oct-impact-modal__footer">
          <button type="button" className="btn oct-impact-modal__btn oct-impact-modal__btn--secondary" onClick={onClose} disabled={loading}>
            <ChevronRight size={16} className="oct-impact-modal__btn-icon--back" /> Back
          </button>
          <button type="button" className="btn oct-impact-modal__btn oct-impact-modal__btn--secondary" onClick={onReassess} disabled={loading}>
            <RefreshCw size={16} /> Reassess
          </button>
          <button type="button" className="btn oct-impact-modal__btn oct-impact-modal__btn--primary" onClick={onProceed} disabled={loading || !!error}>
            <ArrowRight size={16} /> Proceed to Remediation
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImpactAnalysisPopup;
